import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { cn } from "@/components/podkit/lib/cn";
import type { EnvironmentDetailsOutletContext } from "@/hooks/use-environment-details-outlet-context";
import type { EnvironmentDetailsRouteHandle } from "@/routes/environments/details/EnvironmentDetailsRoute";
import { Suspense, type FC } from "react";
import { Outlet, useMatches } from "react-router-dom";

export type DetailsTabPanelProps = {
    environmentId: string;
    isHidden: boolean;
};

/**
 * Check if the current route needs full height (no ScrollArea wrapper).
 * This is determined by the route's handle.fullHeight property.
 */
const useNeedsFullHeight = (): boolean => {
    const matches = useMatches();
    // Check the last (most specific) match for the fullHeight flag
    const lastMatch = matches[matches.length - 1];
    const handle = lastMatch?.handle as EnvironmentDetailsRouteHandle | undefined;
    return handle?.fullHeight === true;
};

export const DetailsTabPanel: FC<DetailsTabPanelProps> = ({ isHidden }) => {
    const needsFullHeight = useNeedsFullHeight();

    const outletContext: EnvironmentDetailsOutletContext = {
        showingAgentConversation: true,
    };

    return (
        <div
            id="details-panel"
            role="tabpanel"
            aria-labelledby="details-tab"
            className={cn("flex min-h-0 flex-1 flex-col", isHidden && "hidden")}
        >
            {needsFullHeight ? (
                // Full height routes (e.g., logs pages) need no ScrollArea wrapper for LogViewer to work properly
                <div className="mx-0.5 my-0.5 flex min-h-0 flex-1 flex-col overflow-hidden p-4">
                    <Suspense>
                        <Outlet context={outletContext} />
                    </Suspense>
                </div>
            ) : (
                <ScrollArea orientation="vertical" className="mx-0.5 my-0.5 h-full gap-6 p-4">
                    <Suspense>
                        <Outlet context={outletContext} />
                    </Suspense>
                </ScrollArea>
            )}
        </div>
    );
};
